import AsyncSearchableSelect from "@/Components/AsyncSearchableSelect/AsyncSearchableSelect";
import ApplicationStoreIcon from "@/UI/icons/ApplicationStoreIcon";
import { throwNewToastError } from "@/Utils/MainUtils";
import { ComboboxItem } from "@mantine/core";
import React, { useMemo, useState } from "react";
import useDebounce from "../../../Hooks/useDebounce/useDebounce";
import { useGetAppsQuery } from "../../../Store/APIs/Apps.api";

interface IApplicationSelectorProps {
  size: "sm" | "lg" | "md";
  placeholder: string;
  selectedApplication: number;
  handleSelectApplication: (appId: number) => void;
  disabled: boolean;
  inError?: boolean;
}

const ApplicationSelector: React.FC<IApplicationSelectorProps> = ({
  size,
  placeholder,
  selectedApplication,
  handleSelectApplication,
  disabled,
  inError,
}) => {
  const [searchFieldValue, setSearchFieldValue] = useState("");

  const debouncedSearchValue = useDebounce(searchFieldValue, 500);

  const { data: allApplicationsList = [] } = useGetAppsQuery({
    limit: 999,
    offset: 0,
    search: null,
  });

  const { data: filteredApplicationsList = [] } = useGetAppsQuery({
    limit: 999,
    offset: 0,
    search: debouncedSearchValue,
  });

  const memoizedItemsList = useMemo(() => {
    return filteredApplicationsList.map((item) => ({
      label: item.name,
      value: String(item.id),
      store: item.url.includes("apple")
        ? ("apple" as const)
        : ("google" as const),
    }));
  }, [filteredApplicationsList]);

  const handleClear = () => {
    handleSelectApplication(0);
    setSearchFieldValue("");
  };

  const selectedApp: ComboboxItem | null = useMemo(() => {
    if (!selectedApplication || allApplicationsList.length === 0) {
      return null;
    }

    const app = allApplicationsList.filter(
      (app) => app.id === selectedApplication
    );

    return {
      label: app[0].name,
      value: String(app[0].id),
    };
  }, [selectedApplication, allApplicationsList]);

  return (
    <AsyncSearchableSelect
      data={memoizedItemsList}
      value={selectedApp}
      onClear={handleClear}
      onSearchChange={setSearchFieldValue}
      onChange={handleSelectApplication}
      searchValue={searchFieldValue}
      clearable
      width={size}
      placeholder={placeholder}
      error={inError}
      disabled={disabled}
      isSelectable={(item) => !item.label.includes("banned")}
      onInvalidSelect={() => throwNewToastError("Application has been banned")}
      renderOption={(item) => (
        <>
          <ApplicationStoreIcon store={item.store} />
          {item.label}
        </>
      )}
    />
  );
};

export default ApplicationSelector;
